import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { Button } from "@/components/base/buttons/button";
import { DeleteAssetTypeModal } from "@/components/Organisms/DeleteAssetTypeModal/DeleteAssetTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import {
	useDisableAssetType,
	useDisableAssetTypeSubOrganization,
	useDuplicateAssetType,
	useEnableAssetType,
	useEnableAssetTypeSubOrganization,
	useUpdateAssetType,
} from "@/services/asset/asset.mutations";
import { assetQueryOptions } from "@/services/asset/asset.options";
import { authQueryOptions } from "@/services/auth/auth.options";
import { organizationQueryOptions } from "@/services/organization/organization.options";
import {
	canShowAccessTab,
	canShowActivityTab,
	getAssetTypeDisplayName,
	getEffectiveAssetTypeDrawerMode,
} from "@/utils/asset-type-detail/asset-type-detail.utils";
import {
	AssetTypeDrawerContext,
	type AssetTypeDrawerContextValue,
} from "@/utils/asset-type-detail/asset-type-drawer-context";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z
		.enum(["drawer", "modal", "full"])
		.optional()
		.default("drawer"),
});

type AssetTypeTab =
	| "details"
	| "access"
	| "schema"
	| "lifecycle"
	| "rules"
	| "activity";

function getCurrentTab(pathname: string): AssetTypeTab {
	const segment = pathname.split("/").filter(Boolean).pop();
	if (
		segment === "details" ||
		segment === "access" ||
		segment === "schema" ||
		segment === "lifecycle" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

const ASSET_TYPE_DRAWER_TAB_ROUTES = {
	details:
		"/org-admin/$orgId/configuration/assetTypes/$assetTypeId/details" as const,
	access:
		"/org-admin/$orgId/configuration/assetTypes/$assetTypeId/access" as const,
	schema:
		"/org-admin/$orgId/configuration/assetTypes/$assetTypeId/schema" as const,
	lifecycle:
		"/org-admin/$orgId/configuration/assetTypes/$assetTypeId/lifecycle" as const,
	rules:
		"/org-admin/$orgId/configuration/assetTypes/$assetTypeId/rules" as const,
	activity:
		"/org-admin/$orgId/configuration/assetTypes/$assetTypeId/activity" as const,
};

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/assetTypes/$assetTypeId",
)({
	component: OrgAssetTypeIdLayout,
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/assetTypes/${params.assetTypeId}`,
		);
		if (exactRoot) {
			throw redirect({
				to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/details",
				params: { orgId: params.orgId, assetTypeId: params.assetTypeId },
				search: { mode: "view", presentation: "drawer" },
			});
		}
	},
});

function OrgAssetTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFormRoute = useRouterState({
		select: (s) =>
			s.matches.some(
				(m) =>
					typeof m.routeId === "string" &&
					m.routeId.endsWith("/$assetTypeId/form"),
			),
	});
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/assetTypes/$assetTypeId",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/assetTypes/$assetTypeId",
	});
	const organizationIdForApi = params.orgId;

	const isFullRoute = /\/configuration\/assetTypes\/[^/]+\/full/.test(pathname);

	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const detailQuery = useQuery(
		assetQueryOptions.typeDetail(params.assetTypeId, {
			organizationId: organizationIdForApi,
		}),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteAssetType",
			entityType: "AssetType",
			entityId: params.assetTypeId,
		}),
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditAssetType",
			entityType: "AssetType",
			entityId: params.assetTypeId,
		}),
	});
	const accessEdit = useTypeAccessEditAllowed(
		"AssetType",
		params.assetTypeId,
	);
	const subOrgCountQuery = useQuery(
		organizationQueryOptions.subOrganizations.count(organizationIdForApi, {}),
	);
	const hasSubOrganizations = (subOrgCountQuery.data ?? 0) > 0;

	const updateMutation = useUpdateAssetType();
	const duplicateMutation = useDuplicateAssetType();
	const { duplicate: duplicateAssetType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (assetTypeId) => ({
				assetTypeId,
				organizationId: organizationIdForApi,
			}),
			onDuplicated: (assetTypeId) => {
				void navigate({
					to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/details",
					params: { orgId: organizationIdForApi, assetTypeId },
					search: { mode: "view", presentation: "drawer" },
				});
			},
			errorMessage: t("admin.assetTypes.modal.createError"),
		});
	const enableMutation = useEnableAssetType();
	const disableMutation = useDisableAssetType();
	const enableSubOrgMutation = useEnableAssetTypeSubOrganization();
	const disableSubOrgMutation = useDisableAssetTypeSubOrganization();
	const isSavingAny =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending ||
		enableSubOrgMutation.isPending ||
		disableSubOrgMutation.isPending;

	const assetType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		assetType?.id,
		assetType?.locationRequired,
	);
	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");
	// Access draft ΓÇö null = org-wide enabled (no sub-orgs), [] = disabled, string[] = selected sub-org IDs
	const [draftSubOrgIds, setDraftSubOrgIds] = useState<string[] | null>(null);

	// Sync access draft only when switching asset types (not on every query refetch).
	useEffect(() => {
		if (!assetType?.id) return;
		const subOrgIds = (assetType.assetTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		setDraftSubOrgIds(
			subOrgIds.length > 0
				? subOrgIds
				: assetType.enabled === false
					? []
					: null,
		);
	}, [assetType?.id, assetType?.assetTypeSubOrganizations, assetType?.enabled]);

	const isDetailLoadingEarly = detailQuery.isLoading;
	const isDetailReadyEarly =
		!isDetailLoadingEarly && !detailQuery.isError && assetType !== null;
	const canEditEarly = !!assetType && isDetailReadyEarly && !!canEditQuery.data;

	useEffect(() => {
		if (isFormRoute) return;
		if (!isDetailReadyEarly || !assetType) return;
		if (search.mode !== "edit") return;
		const currentTabEarly = getCurrentTab(pathname);
		if (currentTabEarly === "access") {
			if (accessEdit.isPending || accessEdit.allowed) return;
		} else if (canEditEarly) {
			return;
		}
		if (isFullRoute) {
			void navigate({
				to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
				params: {
					orgId: params.orgId,
					assetTypeId: params.assetTypeId,
				},
				search: {
					tab:
						currentTabEarly === "access"
							? "access"
							: currentTabEarly === "schema"
								? "form"
								: currentTabEarly === "lifecycle"
									? "lifecycle"
									: currentTabEarly === "rules"
										? "rules"
										: currentTabEarly === "activity"
											? "activity"
											: "details",
					mode: "view",
					presentation: search.presentation,
				},
			});
			return;
		}
		void navigate({
			to: `/org-admin/${params.orgId}/configuration/assetTypes/${params.assetTypeId}/${currentTabEarly}`,
			search: { mode: "view", presentation: search.presentation },
		});
	}, [
		assetType,
		accessEdit.allowed,
		accessEdit.isPending,
		canEditEarly,
		isDetailReadyEarly,
		isFormRoute,
		isFullRoute,
		navigate,
		params.assetTypeId,
		params.orgId,
		pathname,
		search.mode,
		search.presentation,
	]);

	// Sync detail drafts from server; never touch access draft while editing the Access tab.
	useEffect(() => {
		if (!assetType) return;
		const currentTab = getCurrentTab(pathname);
		const isAccessEdit =
			(search.mode ?? "view") === "edit" && currentTab === "access";
		const isDetailsEdit = (search.mode ?? "view") === "edit";
		const name = getAssetTypeDisplayName(assetType);
		const description = assetType.description ?? "";
		const icon = assetType.icon ?? "";
		if (!isDetailsEdit || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (!isDetailsEdit || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (!isDetailsEdit || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
		if (isAccessEdit) return;
		const subOrgIds = (assetType.assetTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		setDraftSubOrgIds(
			subOrgIds.length > 0
				? subOrgIds
				: assetType.enabled === false
					? []
					: null,
		);
	}, [assetType, pathname, search.mode]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/org-admin/$orgId/configuration/assetTypes",
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	if (isFormRoute) {
		return (
			<div className="flex min-h-0 flex-1 flex-col bg-background-primary">
				<Outlet />
			</div>
		);
	}

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || assetType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && assetType !== null;

	const loadedAssetType = assetType ?? undefined;
	const displayAssetType =
		loadedAssetType && isDetailReady
			? {
					...loadedAssetType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon || loadedAssetType.icon,
				}
			: null;

	const canEdit = !!loadedAssetType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedAssetType &&
		isDetailReady &&
		canShowActivityTab("organization", loadedAssetType);
	const showAccess = canShowAccessTab("organization");
	const currentTab = getCurrentTab(pathname);
	const canEditAccess = currentTab === "access" && accessEdit.allowed;
	const drawerMode = getEffectiveAssetTypeDrawerMode(
		canEdit || canEditAccess,
		search.mode ?? "view",
	);
	const isEditing = drawerMode === "edit";
	const isValid = draftName.trim().length > 0;

	const tabs: { id: AssetTypeTab; label: string }[] = isDetailReady
		? [
				{ id: "details", label: t("admin.assetTypes.drawer.tabs.details") },
				...(showAccess
					? [
							{
								id: "access" as const,
								label: t("admin.assetTypes.drawer.tabs.access"),
							},
						]
					: []),
				{ id: "schema", label: t("admin.assetTypes.drawer.tabs.form") },
				{ id: "lifecycle", label: t("admin.assetTypes.drawer.tabs.lifecycle") },
				{ id: "rules", label: t("admin.assetTypes.drawer.tabs.rules") },
				...(showActivity
					? [
							{
								id: "activity" as const,
								label: t("admin.assetTypes.drawer.tabs.activity"),
							},
						]
					: []),
			]
		: [];
	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ id: currentTabInfo.id, label: `Edit ${currentTabInfo.label}` }]
		: [];

	const canDelete =
		!!loadedAssetType &&
		isDetailReady &&
		!!canDeleteQuery.data &&
		!isEditing;
	const canDuplicate = isDetailReady && !isEditing;

	const saveDraftFn = async (): Promise<void> => {
		const isAccessSave = currentTab === "access";
		if (
			!loadedAssetType ||
			!isValid ||
			(isAccessSave ? !accessEdit.allowed : !canEdit) ||
			isSavingAny
		)
			return;
		try {
			if (currentTab === "access") {
				if (hasSubOrganizations) {
					const serverIds = (loadedAssetType.assetTypeSubOrganizations ?? [])
						.map((s) => s.id)
						.filter((id): id is string => id !== null);
					const nextIds = draftSubOrgIds ?? [];
					const toEnable = nextIds.filter((id) => !serverIds.includes(id));
					const toDisable = serverIds.filter((id) => !nextIds.includes(id));
					await Promise.all([
						...toEnable.map((subOrgId) =>
							enableSubOrgMutation.mutateAsync({
								assetTypeId: loadedAssetType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
						...toDisable.map((subOrgId) =>
							disableSubOrgMutation.mutateAsync({
								assetTypeId: loadedAssetType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
					]);
				} else {
					if (draftSubOrgIds === null) {
						await enableMutation.mutateAsync({
							assetTypeId: loadedAssetType.id,
							organizationId: organizationIdForApi,
						});
					} else {
						await disableMutation.mutateAsync({
							assetTypeId: loadedAssetType.id,
							organizationId: organizationIdForApi,
						});
					}
				}
				showSuccessToast({
					message: t("admin.assetTypes.drawer.access.toast.saveSuccess"),
				});
			} else {
				const currentSubOrgIds = (
					loadedAssetType.assetTypeSubOrganizations ?? []
				)
					.map((s) => s.id)
					.filter((id): id is string => id !== null);
				await updateMutation.mutateAsync({
					assetTypeId: loadedAssetType.id,
					data: {
						name: draftName.trim(),
						description: draftDescription || null,
						icon: draftIcon || null,
						typeId: loadedAssetType.id,
						statusLifecycleId: loadedAssetType.statusLifecycle?.id ?? null,
						geometryType: loadedAssetType.geometryType ?? null,
						subOrganizationIds:
							currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
						locationRequired,
					},
					organizationId: organizationIdForApi,
				});
				setEditedName(draftName.trim());
				setEditedDescription(draftDescription);
				setEditedIcon(draftIcon);
				showSuccessToast({
					message: t("admin.assetTypes.drawer.details.toast.saveSuccess"),
				});
			}
			if (!isFullRoute) {
				void navigate({
					to: ASSET_TYPE_DRAWER_TAB_ROUTES[currentTab],
					params: {
						orgId: params.orgId,
						assetTypeId: params.assetTypeId,
					},
					search: { mode: "view", presentation: search.presentation },
				});
			}
		} catch {
			// Global mutation error handler in root-provider.tsx already shows the error toast
		}
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedAssetType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		const currentSubOrgIds = (loadedAssetType.assetTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			assetTypeId: loadedAssetType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				typeId: loadedAssetType.id,
				statusLifecycleId: loadedAssetType.statusLifecycle?.id ?? null,
				geometryType: loadedAssetType.geometryType ?? null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired,
			},
			organizationId: organizationIdForApi,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedAssetType) return;
		const currentSubOrgIds = (loadedAssetType.assetTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			assetTypeId: loadedAssetType.id,
			data: {
				name: (editedName || loadedAssetType.name || "").trim(),
				description: editedDescription || loadedAssetType.description || null,
				icon: editedIcon || loadedAssetType.icon || null,
				typeId: loadedAssetType.id,
				statusLifecycleId: loadedAssetType.statusLifecycle?.id ?? null,
				geometryType: loadedAssetType.geometryType ?? null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired: next,
			},
			organizationId: organizationIdForApi,
		});
		setLocationRequired(next);
	};

	const contextValue: AssetTypeDrawerContextValue | null =
		loadedAssetType && isDetailReady && displayAssetType
			? {
					scope: "organization",
					assetType: displayAssetType,
					mode: drawerMode,
					activeTab: currentTab,
					setMode: (mode) => {
						const effectiveMode = getEffectiveAssetTypeDrawerMode(
							canEdit || canEditAccess,
							mode,
						);
						if (isFullRoute) {
							void navigate({
								to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
								params: {
									orgId: params.orgId,
									assetTypeId: params.assetTypeId,
								},
								search: {
									tab:
										currentTab === "access"
											? "access"
											: currentTab === "schema"
												? "form"
												: currentTab === "lifecycle"
													? "lifecycle"
													: currentTab === "rules"
														? "rules"
														: currentTab === "activity"
															? "activity"
															: "details",
									mode: effectiveMode,
									presentation: search.presentation,
								},
							});
						} else {
							void navigate({
								to: ASSET_TYPE_DRAWER_TAB_ROUTES[currentTab],
								params: {
									orgId: params.orgId,
									assetTypeId: params.assetTypeId,
								},
								search: {
									mode: effectiveMode,
									presentation: search.presentation,
								},
							});
						}
					},
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds,
					setDraftSubOrgIds,
					isSaving: isSavingAny,
					isValid,
					isDirty:
						draftName.trim() !== editedName ||
						draftDescription !== editedDescription ||
						draftIcon !== editedIcon,
					saveDraft: saveDraftFn,
					cancelDraft: () => {
						setDraftName(editedName);
						setDraftDescription(editedDescription);
						setDraftIcon(editedIcon);
						const subOrgIds = (loadedAssetType.assetTypeSubOrganizations ?? [])
							.map((s) => s.id)
							.filter((id): id is string => id !== null);
						setDraftSubOrgIds(
							subOrgIds.length > 0
								? subOrgIds
								: loadedAssetType.enabled === false
									? []
									: null,
						);
						if (!isFullRoute) {
							void navigate({
								to: ASSET_TYPE_DRAWER_TAB_ROUTES[currentTab],
								params: {
									orgId: params.orgId,
									assetTypeId: params.assetTypeId,
								},
								search: { mode: "view", presentation: search.presentation },
							});
						}
					},
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayAssetType !== null
			? getAssetTypeDisplayName(displayAssetType)
			: t("admin.assetTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedAssetType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedAssetType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedAssetType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedAssetType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	const editFooter =
		isEditing && currentTab !== "access" && contextValue ? (
			<div className="flex w-full items-center justify-end gap-3">
				<Button
					data-testid="assettype-details-cancel-btn"
					color="secondary"
					size="md"
					onClick={contextValue.cancelDraft}
					isDisabled={isSavingAny}
				>
					{t("admin.assetTypes.drawer.actions.cancel")}
				</Button>
				<Button
					data-testid="assettype-details-save-btn"
					color="primary"
					size="md"
					onClick={() => void contextValue.saveDraft()}
					isDisabled={!isValid || isSavingAny}
					isLoading={isSavingAny}
				>
					{t("admin.assetTypes.drawer.actions.save")}
				</Button>
			</div>
		) : undefined;

	if (isFullRoute) {
		return contextValue ? (
			<AssetTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</AssetTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	const isOnAssetTypeSubroute = /\/configuration\/assetTypes\/[^/]+\//.test(
		pathname,
	);
	if (!isOnAssetTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="asset"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
						params: {
							orgId: params.orgId,
							assetTypeId: params.assetTypeId,
						},
						search: {
							tab: "details",
							mode: "view",
							presentation: "full",
						},
					});
				}}
				duplicateLabel={
					canDuplicate
						? t("admin.assetTypes.table.actions.duplicate")
						: undefined
				}
				onDuplicate={
					canDuplicate
						? () => duplicateAssetType(params.assetTypeId)
						: undefined
				}
				deleteLabel={
					canDelete
						? t("admin.assetTypes.drawer.footer.deleteAssetType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.assetTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.assetTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/org-admin/${params.orgId}/configuration/assetTypes/${params.assetTypeId}/${tabId}`,
						search: {
							mode: drawerMode,
							presentation: search.presentation,
						},
					})
				}
				footer={editFooter}
				data-testid="asset-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<AssetTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</AssetTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteAssetTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={handleClose}
				assetTypeId={params.assetTypeId}
				assetTypeName={
					loadedAssetType ? getAssetTypeDisplayName(loadedAssetType) : ""
				}
				organizationId={organizationIdForApi}
				scope="organization"
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.assetTypes.duplicate.inProgress")}
				testId="assettype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
